import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { showToast } from "../../../../../utils/toast";
import {
  adminListTaxFormsAPI,
} from "../../../../../api/taxFormsApi";
import { getFileByIdAPI } from "../../../../../api/userApi";
import AdminPayoutsAndTaxFormsTabs from "../AdminPayoutsAndTaxFormsTabs";
import TaxFormsTable from "./TaxFormsTable";
import theme from "../../../../../theme";
import { SearchIcon } from "lucide-react";

type TaxFormType = "W9" | "W8BEN";
type TaxFormStatus = "pending" | "verified" | "rejected";

export interface ITaxForm {
  _id: string;
  userId: string;
  user: {
    username: string
  },
  type: TaxFormType;
  status: TaxFormStatus;
  fileId?: string;
  mediaUrl?: string;
  originalName?: string;
  mimeType?: string;
  submittedAt?: string;
  verifiedAt?: string;
  verifiedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectedNote?: string;
  userCountry?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface IListRes {
  items: ITaxForm[];
  total: number;
  page: number;
  limit: number;
}

const useStyles = makeStyles((theme) => ({
  container: { padding: 24, color: "#fff" },

  topBar: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    marginBottom: 12
  },
  spacer: { flex: 1 },

  filters: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(140px, 1fr))",
    gap: 12,
    marginBottom: 16,
  },

  tableWrap: {
    background: "#141414",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.08)",
  },

  tableHeaderCell: {
    color: "#fff",
    fontWeight: 500,
    fontSize: 16,
    background: "#74747426",
    borderRight: "1px solid #74747426",
    borderBottom: 0,
    lineHeight: "normal",
    height: "auto",
    verticalAlign: "center",

    "&.active": {
      color: "#FF78AA",
    },
    "& .MuiOutlinedInput-inputAdornedStart": {
      background: "transparent",
      border: 0,
    },
  },

  tableBodyCell: {
    color: "#fff",
    border: "1px solid #74747426",
    fontSize: 14,
    lineHeight: "normal",
    height: "auto",
  },
  actionBtnRow: {
    position: "relative",
    whiteSpace: "nowrap",
    display: "flex",
    gap: 8,
    justifyContent: "flex-start",
    minHeight: "48px",
  },
  actionButtons: {
    textTransform: "none",
    borderRadius: 6,
    maxHeight: "34px",
    "& .MuiButton-label": {
      display: "flex",
      gap: 4
    }
  },
  verifyButton: {
    fontSize: "14px",
    backgroundColor: "#85F96B",
  },
  rejectButton: {
    color: "#fff",
    borderColor: "#FF4977",
    fontSize: "14px",
  },

  noBorder: { border: 0 },

  searchInput: {
    color: "#fff",
    marginRight: 8,
    cursor: "pointer",
  },

  textFieldRoot: {
    "& .MuiOutlinedInput-adornedStart": {
      maxWidth: "165px",
      paddingLeft: 8,
      marginRight: 12,
    },
    "& .MuiOutlinedInput-adornedStart, & .MuiInputBase-input": {
      background: "#131313",
      borderRadius: 4,
    },
  },

  inputRoot: { color: "#fff" },
  inputLabel: { color: "rgba(255,255,255,0.9)" },

  outlined: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.35)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.6)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
  },

  menuPaper: { background: "#121212", color: "#fff" },
  dialogPaper: { background: "#0f0f0f", color: "#fff" },

  paginationRoot: { color: "#fff", fontSize: "16px" },
  paginationCaption: { color: "rgba(255,255,255,0.8)", fontSize: "16px" },
  paginationSelectIcon: { color: "#fff", fontSize: "16px" },

  outlinedIconButton: {
    color: "#fff",
    border: "1px solid #74747480",
    borderRadius: 6,
    padding: 8,
    fontSize: 14,
  },

  exportPayoutsButton: {
    color: "#fff",
    fontSize: "20px",
    fontWeight: 600,
    border: "1px solid #FE5A97 !important",
    padding: "12px 20px",
    borderRadius: 6,
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      padding: "9px 15px",
    },
  },

  previewBox: {
    display: "grid",
    placeItems: "center",
    width: "100%",
    height: "70vh",
    background: "#111",
  },
  previewImg: { maxWidth: "100%", maxHeight: "100%" },
  previewIframe: { width: "100%", height: "100%", border: 0 },

  ellipsisCell: {
    maxWidth: 300,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  userNameLink: {
    color: "white",
    textDecoration: "underline",
    textDecorationColor: "white",
    "&:hover": {
      color: "#FE5A97",
      textDecorationColor: "#FE5A97"
    }
  }
}));

type UserLocal = {
  role?: string;
  roles?: string[];
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  userType?: { title?: string };
};
const isUserAdmin = (u?: UserLocal | null) =>
  !!u &&
  (u.isAdmin === true ||
    u.isSuperAdmin === true ||
    String(u.role || "").toLowerCase() === "admin" ||
    String(u.userType?.title || "").toLowerCase() === "admin" ||
    (Array.isArray(u.roles) &&
      u.roles
        .map(r => String(r).toLowerCase())
        .some(
          r => r === "admin" || r === "superadmin" || r === "super-admin"
        )));

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleString() : "—");

// витягнути id з /api/files/<id>
const extractFileId = (mediaUrl?: string) =>
  mediaUrl && mediaUrl.includes("/api/files/")
    ? mediaUrl.split("/api/files/")[1].split(/[?#/]/)[0]
    : undefined;

const AdminTaxFormsPage: React.FC = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // admin gate
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("user");
      const parsed = raw ? (JSON.parse(raw) as UserLocal) : null;
      setIsAdminUser(isUserAdmin(parsed));
    } catch {
      setIsAdminUser(false);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  // filters & paging
  const [status, setStatus] = useState<"" | TaxFormStatus>("");
  const [type, setType] = useState<"" | TaxFormType>("");
  const [creatorUsername, setCreatorUsername] = useState<string>("");

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // data
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ITaxForm[]>([]);
  const [total, setTotal] = useState(0);

  // preview file dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<string>(
    "application/octet-stream"
  );
  const [previewTitle, setPreviewTitle] = useState<string>("file");
  const revokeRef = useRef<null | (() => void)>(null);

  const load = async (p = page, l = limit, tmpCreatorUsername?: string, filter?: { status?: TaxFormStatus; type?: TaxFormType }) => {
    setLoading(true);
    try {
      const res = (await adminListTaxFormsAPI({
        status: filter?.status ?? status ?? undefined,
        type: filter?.type ?? type ?? undefined,
        username: tmpCreatorUsername !== undefined ? tmpCreatorUsername || undefined : creatorUsername || undefined,
        country: undefined,
        from: undefined,
        to: undefined,
        page: p + 1,
        limit: l,
        sortBy,
        sortDir,
      } as any)) as IListRes;

      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error(e);
      showToast({ message: "Failed to load tax forms", err: true });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setPage(0);
    load(0, limit);
  };

  useEffect(
    () => {
      if (isAdminUser) load(0, limit);
    },
    /* eslint-disable-next-line */
    [isAdminUser]
  );


  // відкриття файлу у модалці
  const previewFile = async (row: ITaxForm) => {
    try {
      const id = row.fileId || extractFileId(row.mediaUrl);
      if (!id) {
        showToast({ message: "File not available", err: true });
        return;
      }

      const res: any = await getFileByIdAPI(id);
      // підтримуємо обидві форми відповіді:
      // 1) { url, type, name, revoke }  2) "blob:<...>"
      let url: string;
      let type: string;
      let name: string;
      let revoke: (() => void) | undefined;

      if (typeof res === "string") {
        url = res;
        type = row.mimeType || "application/octet-stream";
        name = row.originalName || "file";
        revoke = () => URL.revokeObjectURL(res);
      } else {
        url = res.url;
        type = res.type || row.mimeType || "application/octet-stream";
        name = res.name || row.originalName || "file";
        revoke = res.revoke;
      }

      // закриваємо попередній blob (якщо був)
      revokeRef.current?.();

      setPreviewUrl(url);
      setPreviewType(type);
      setPreviewTitle(name);
      revokeRef.current = revoke || null;
      setPreviewOpen(true);
    } catch (e) {
      console.error(e);
      showToast({ message: "Failed to open file", err: true });
    }
  };

  const closePreview = () => {
    revokeRef.current?.();
    revokeRef.current = null;
    setPreviewUrl(null);
    setPreviewOpen(false);
  };

  // gates
  if (!authChecked) {
    return (
      <Box
        className={classes.container}
        style={{ display: "flex", justifyContent: "center", paddingTop: 40 }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!isAdminUser) {
    return (
      <Box className={classes.container} style={{ textAlign: "center" }}>
        <Typography variant="h5">403 — Admins only</Typography>
        <Typography style={{ opacity: 0.7, marginTop: 8 }}>
          You don&apos;t have access to this page.
        </Typography>
      </Box>
    );
  }

  //main UI
  return (
    <Box className={classes.container}>

      <AdminPayoutsAndTaxFormsTabs />
      {isMobile && (
        <TextField
          autoFocus
          size="small"
          variant="outlined"
          className={classes.textFieldRoot}
          placeholder="Username"
          value={creatorUsername}
          onChange={(e) => setCreatorUsername(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyFilters();
          }}
          InputProps={{
            classes: {
              input: classes.tableHeaderCell,
              notchedOutline: classes.noBorder,
            },
            startAdornment: (
              <InputAdornment position="start" onClick={applyFilters}>
                <SearchIcon
                  fontSize="small"
                  size={16}
                  className={classes.searchInput}
                />
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* <Typography variant="h5" style={{ marginBottom: 8 }}>
        Admin · Tax Forms
      </Typography> */}

      {/* Filters */}
      {/* <Box className={classes.filters}>
        <TextField
          select
          variant="outlined"
          size="small"
          label="Status"
          value={status}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setStatus(e.target.value as TaxFormStatus | "")
          }
          className={classes.outlined}
          InputProps={{ className: classes.inputRoot }}
          InputLabelProps={{ className: classes.inputLabel, shrink: true }}
          SelectProps={{ MenuProps: { classes: { paper: classes.menuPaper } } }}
        >
          <MenuItem style={{ color: "#fff" }} value="">
            All
          </MenuItem>
          <MenuItem style={{ color: "#fff" }} value="pending">
            Pending
          </MenuItem>
          <MenuItem style={{ color: "#fff" }} value="verified">
            Verified
          </MenuItem>
          <MenuItem style={{ color: "#fff" }} value="rejected">
            Rejected
          </MenuItem>
        </TextField>

        <TextField
          select
          variant="outlined"
          size="small"
          label="Type"
          value={type}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setType(e.target.value as TaxFormType | "")
          }
          className={classes.outlined}
          InputProps={{ className: classes.inputRoot }}
          InputLabelProps={{ className: classes.inputLabel, shrink: true }}
          SelectProps={{ MenuProps: { classes: { paper: classes.menuPaper } } }}
        >
          <MenuItem style={{ color: "#fff" }} value="">
            All
          </MenuItem>
          <MenuItem style={{ color: "#fff" }} value="W9">
            W-9 (US)
          </MenuItem>
          <MenuItem style={{ color: "#fff" }} value="W8BEN">
            W-8BEN (Non-US)
          </MenuItem>
        </TextField>

        <TextField
          variant="outlined"
          size="small"
          label="UserId"
          value={userId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setUserId(e.target.value)
          }
          placeholder="5f... ObjectId"
          className={classes.outlined}
          InputProps={{ className: classes.inputRoot }}
          InputLabelProps={{ className: classes.inputLabel, shrink: true }}
        />

        <div />
        <Button
          variant="contained"
          color="primary"
          onClick={applyFilters}
          style={{ height: 40, alignSelf: "end" }}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setStatus("");
            setType("");
            setUserId("");
            setPage(0);
            load(0, limit);
          }}
          style={{
            height: 40,
            alignSelf: "end",
            color: "#fff",
            borderColor: "rgba(255,255,255,0.4)",
          }}
        >
          Reset
        </Button>
        {loading && (
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <CircularProgress size={22} />
          </div>
        )}
      </Box> */}

      {/* Table */}
      <TaxFormsTable items={items} sortBy={sortBy} setSortBy={setSortBy} sortDir={sortDir} setSortDir={setSortDir}
        previewFile={previewFile} setPage={setPage} load={load} loading={loading} limit={limit} creatorUsername={creatorUsername} setCreatorUsername={setCreatorUsername}
        type={type} setType={setType} status={status} setStatus={setStatus} applyFilters={applyFilters} />

      <Box display="flex" justifyContent="center">
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => {
            setPage(p);
            load(p, limit);
          }}
          rowsPerPage={limit}
          onRowsPerPageChange={e => {
            const v = parseInt((e.target as HTMLInputElement).value, 10);
            setLimit(v);
            setPage(0);
            load(0, v);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          classes={{
            root: classes.paginationRoot,
            caption: classes.paginationCaption,
            selectIcon: classes.paginationSelectIcon,
          }}
          SelectProps={{ MenuProps: { classes: { paper: classes.menuPaper } } }}
        />
      </Box>

      {/* Preview file dialog — FIXED */}
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>{previewTitle}</DialogTitle>
        <DialogContent dividers>
          <Box className={classes.previewBox}>
            {previewUrl ? (
              previewType.startsWith("image/") ? (
                <img
                  className={classes.previewImg}
                  src={previewUrl}
                  alt={previewTitle}
                />
              ) : previewType.includes("pdf") ? (
                <object
                  data={previewUrl}
                  type="application/pdf"
                  className={classes.previewIframe}
                >
                  <div style={{ textAlign: "center" }}>
                    PDF preview is not supported by this browser.
                    <br />
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in new tab
                    </a>
                  </div>
                </object>
              ) : (
                <iframe
                  className={classes.previewIframe}
                  src={previewUrl}
                  title={previewTitle}
                />
              )
            ) : (
              <CircularProgress />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {previewUrl && (
            <a
              href={previewUrl}
              download={previewTitle}
              style={{ textDecoration: "none" }}
            >
              <Button
                variant="outlined"
                style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
              >
                Download
              </Button>
            </a>
          )}
          <Button onClick={closePreview} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default AdminTaxFormsPage;
